import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Faq, NavGroup, Pillar, PostCard, SubNav } from '@/types'

import { ArrowRight, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Container } from '@/components/generals/container'
import { ArticleBody } from '@/components/content/article-body'
import { ClusterList } from '@/components/content/cluster-list'
import { EditorialByline } from '@/components/content/editorial-byline'
import { FaqSection } from '@/components/content/faq-section'
import { FeaturedArticles } from '@/components/content/featured-articles'
import { NavGroupArticles } from '@/components/content/nav-group-articles'
import { MedicalDisclaimer } from '@/components/content/medical-disclaimer'
import { TldrBox } from '@/components/content/tldr-box'
import { accentClasses } from '@/constants/hubs'
import { pillarIcon } from '@/constants/pillars'
import { breadcrumbSchema, faqSchema, pillarSchema } from '@/lib/structured-data'
import {
    getNavGroupBySlug,
    getNavGroups,
    getPillarBySlug,
    getPillarFaqs,
    getPillars,
    getPillarsByNavGroup,
    getPostsByNavGroup,
    getPostsByPillar,
    getSubNav
} from '@/lib/queries'

type SectionProps =
    | { kind: 'pillar'; pillar: Pillar; posts: PostCard[]; faqs: Faq[]; subNav: SubNav | null }
    | {
          kind: 'navGroup'
          navGroup: NavGroup
          pillars: Pillar[]
          posts: PostCard[]
          subNav: SubNav | null
      }

/* -------------------------------------------------------------------------- */
/*  Pillar hub: a real, comprehensive content asset                           */
/* -------------------------------------------------------------------------- */

const PillarView: FC<{
    pillar: Pillar
    posts: PostCard[]
    faqs: Faq[]
    subNav: SubNav | null
}> = ({ pillar, posts, faqs, subNav }): ReactNode => {
    const Icon = pillarIcon(pillar.slug)
    const colors = accentClasses(pillar.accent)

    // Curated picks. getPostsByPillar already sorts featured first, so this is
    // just the top slice of that ordering rather than a second query.
    const featured = posts.filter((post) => post.featured).slice(0, 3)

    const breadcrumb = breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: pillar.name, path: `/${pillar.slug}` }
    ])

    const graphs = [pillarSchema(pillar, posts, pillar.updatedAt), breadcrumb]
    if (faqs.length > 0) graphs.push(faqSchema(faqs))

    return (
        <Layout subNav={subNav}>
            <Seo
                title={pillar.seoTitle}
                description={pillar.description}
                canonical={`/${pillar.slug}`}
                ogImage={pillar.coverImage ?? undefined}
                ogImageAlt={pillar.coverImageAlt ?? pillar.h1}
                type='article'
                section={pillar.navGroupName ?? undefined}
                modifiedTime={pillar.updatedAt}
                jsonLd={graphs}
            />

            <section className='border-b border-border bg-secondary/30'>
                <Container className='py-14 sm:py-20'>
                    <nav className='mb-6 flex items-center gap-1.5 text-sm text-muted-foreground'>
                        <Link href='/' className='transition-colors hover:text-foreground'>
                            Home
                        </Link>
                        <ChevronRight className='size-3.5' />
                        <span
                            data-mdlfy-cms={`pillars:${pillar.id}:name`}
                            className='text-foreground'
                        >
                            {pillar.name}
                        </span>
                    </nav>

                    <span
                        className={cn(
                            'flex size-12 items-center justify-center rounded-2xl',
                            colors.bgSoft,
                            colors.text
                        )}
                    >
                        <Icon className='size-6' />
                    </span>

                    <h1
                        data-mdlfy-cms={`pillars:${pillar.id}:h1`}
                        className='mt-5 max-w-3xl font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl'
                    >
                        {pillar.h1}
                    </h1>

                    <p
                        data-mdlfy-cms={`pillars:${pillar.id}:tagline`}
                        className='mt-4 max-w-2xl text-lg leading-relaxed text-foreground/80'
                    >
                        {pillar.tagline}
                    </p>

                    <p className='mt-6 text-sm text-muted-foreground'>
                        {posts.length} {posts.length === 1 ? 'article' : 'articles'} in this guide
                    </p>
                </Container>
            </section>

            <Container size='narrow' className='py-14 sm:py-16'>
                <EditorialByline updatedAt={pillar.updatedAt} />

                {pillar.tldr && <TldrBox content={pillar.tldr} className='mt-10' />}

                {/* The exercise library lives at /exercises, outside the pillar
                    folders, so the Movement guide points to it here. */}
                {pillar.slug === 'movement' && (
                    <Link
                        href='/exercises'
                        className='group mt-10 flex items-center justify-between gap-4 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4 no-underline transition-colors hover:border-primary/60'
                    >
                        <span>
                            <span className='block font-heading text-lg font-semibold text-foreground'>
                                Exercise library and muscle map
                            </span>
                            <span className='mt-0.5 block text-sm text-muted-foreground'>
                                Tap any muscle to find exercises for it, with step-by-step form guides.
                            </span>
                        </span>
                        <ArrowRight className='size-5 shrink-0 text-primary transition-transform group-hover:translate-x-0.5' />
                    </Link>
                )}

                <FeaturedArticles
                    posts={featured}
                    pillarName={pillar.name}
                    className='mt-12'
                />

                {pillar.body ? (
                    <div className='mt-10'>
                        <ArticleBody content={pillar.body} />
                    </div>
                ) : (
                    <p
                        data-mdlfy-cms={`pillars:${pillar.id}:description`}
                        className='mt-10 leading-relaxed text-muted-foreground'
                    >
                        {pillar.description}
                    </p>
                )}
            </Container>

            <section className='border-t border-border bg-secondary/30'>
                <Container size='narrow' className='py-14 sm:py-16'>
                    <h2 className='font-heading text-2xl font-semibold tracking-tight text-foreground'>
                        Everything in this guide
                    </h2>
                    <p className='mt-3 text-muted-foreground'>
                        Each article below goes deeper on one part of {pillar.name.toLowerCase()}.
                    </p>

                    <ClusterList posts={posts} className='mt-10' />
                </Container>
            </section>

            <Container size='narrow' className='py-14 sm:py-16'>
                <FaqSection faqs={faqs} />
                <MedicalDisclaimer className={faqs.length > 0 ? 'mt-12' : undefined} />
            </Container>
        </Layout>
    )
}

/* -------------------------------------------------------------------------- */
/*  Nav group: a thin contextual directory, deliberately noindexed             */
/* -------------------------------------------------------------------------- */

const NavGroupView: FC<{
    navGroup: NavGroup
    pillars: Pillar[]
    posts: PostCard[]
    subNav: SubNav | null
}> = ({ navGroup, pillars, posts, subNav }): ReactNode => {
    return (
        <Layout subNav={subNav}>
            {/*
                Noindexed on purpose: this page is a browsing aid, not a content
                asset, and indexing it would let it cannibalise the pillar pages
                it links to. `follow` is deliberate, so the link equity still
                passes through to those pillars.
            */}
            <Seo
                title={navGroup.name}
                description={navGroup.description}
                canonical={`/${navGroup.slug}`}
                noindex
            />

            <section className='border-b border-border bg-secondary/30'>
                <Container className='py-14 sm:py-20'>
                    <nav className='mb-6 flex items-center gap-1.5 text-sm text-muted-foreground'>
                        <Link href='/' className='transition-colors hover:text-foreground'>
                            Home
                        </Link>
                        <ChevronRight className='size-3.5' />
                        <span className='text-foreground'>{navGroup.name}</span>
                    </nav>

                    <h1
                        data-mdlfy-cms={`hubs:${navGroup.id}:name`}
                        className='font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl'
                    >
                        {navGroup.name}
                    </h1>

                    <p
                        data-mdlfy-cms={`hubs:${navGroup.id}:tagline`}
                        className='mt-4 max-w-2xl text-lg leading-relaxed text-foreground/80'
                    >
                        {navGroup.tagline}
                    </p>
                </Container>
            </section>

            {/* The guides come first: they are the real content assets here. */}
            {pillars.length > 0 && (
                <section id='guides' className='scroll-mt-20 border-b border-border'>
                    <Container className='py-14 sm:py-16'>
                        <h2 className='font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                            Start with a guide
                        </h2>
                        <p className='mt-2 max-w-2xl text-muted-foreground'>
                            Each one is a complete, evidence-aware guide to its topic, with every
                            related article gathered beneath it.
                        </p>

                        <ul className='mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                            {pillars.map((pillar) => {
                                const Icon = pillarIcon(pillar.slug)
                                const colors = accentClasses(pillar.accent)
                                const count = posts.filter(
                                    (post) => post.pillarSlug === pillar.slug
                                ).length

                                return (
                                    <li key={pillar.id} className='flex'>
                                        <Link
                                            href={`/${pillar.slug}`}
                                            className='group flex flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-primary/40'
                                        >
                                            <span className='relative block aspect-[16/10] overflow-hidden bg-muted'>
                                                {pillar.coverImage && (
                                                    <img
                                                        src={pillar.coverImage}
                                                        alt=''
                                                        loading='lazy'
                                                        className='absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                                                    />
                                                )}
                                            </span>

                                            <span className='flex flex-1 flex-col p-6'>
                                                <span
                                                    className={cn(
                                                        'flex size-11 items-center justify-center rounded-xl',
                                                        colors.bgSoft,
                                                        colors.text
                                                    )}
                                                >
                                                    <Icon className='size-5' />
                                                </span>

                                                <span
                                                    data-mdlfy-cms={`pillars:${pillar.id}:h1`}
                                                    className='mt-5 block font-heading text-xl font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary'
                                                >
                                                    {pillar.h1}
                                                </span>

                                                <span
                                                    data-mdlfy-cms={`pillars:${pillar.id}:tagline`}
                                                    className='mt-2 block flex-1 text-sm leading-relaxed text-muted-foreground'
                                                >
                                                    {pillar.tagline}
                                                </span>

                                                <span className='mt-5 flex items-center justify-between border-t border-border pt-4 text-sm'>
                                                    <span className='text-muted-foreground'>
                                                        {count} {count === 1 ? 'article' : 'articles'}
                                                    </span>
                                                    <span
                                                        className={cn(
                                                            'inline-flex items-center gap-1.5 font-medium',
                                                            colors.text
                                                        )}
                                                    >
                                                        Read the guide
                                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-1' />
                                                    </span>
                                                </span>
                                            </span>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </Container>
                </section>
            )}

            {/* Then every article in the section, filterable by topic. */}
            <section id='articles' className='scroll-mt-20 bg-secondary/30'>
                <Container className='py-14 sm:py-16'>
                    <NavGroupArticles posts={posts} pillars={pillars} />
                </Container>
            </section>
        </Layout>
    )
}

const SectionPage: FC<SectionProps> = (props): ReactNode => {
    if (props.kind === 'pillar') {
        return (
            <PillarView
                pillar={props.pillar}
                posts={props.posts}
                faqs={props.faqs}
                subNav={props.subNav}
            />
        )
    }

    return (
        <NavGroupView
            navGroup={props.navGroup}
            pillars={props.pillars}
            posts={props.posts}
            subNav={props.subNav}
        />
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const [pillars, navGroups] = await Promise.all([getPillars(), getNavGroups()])

    return {
        paths: [...pillars, ...navGroups].map((entry) => ({ params: { section: entry.slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<SectionProps> = async ({ params }) => {
    const slug = typeof params?.section === 'string' ? params.section : ''

    // Pillars win over nav groups: they are the indexable content assets.
    const pillar = await getPillarBySlug(slug)

    if (pillar) {
        const [posts, faqs, subNav] = await Promise.all([
            getPostsByPillar(pillar.slug),
            getPillarFaqs(pillar.id),
            getSubNav(pillar.navGroupSlug)
        ])

        return {
            props: { kind: 'pillar', pillar, posts, faqs, subNav },
            revalidate: 300
        }
    }

    const navGroup = await getNavGroupBySlug(slug)

    if (navGroup) {
        const [pillars, posts, subNav] = await Promise.all([
            getPillarsByNavGroup(navGroup.slug),
            getPostsByNavGroup(navGroup.slug),
            getSubNav(navGroup.slug)
        ])

        return {
            props: { kind: 'navGroup', navGroup, pillars, posts, subNav },
            revalidate: 300
        }
    }

    return { notFound: true, revalidate: 60 }
}

export default SectionPage
